import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { MetaChip } from '../../components';
import { P, pillSt } from '../../theme/tokens';
import { SCORE_FOOTNOTE, SCORE_MODEL, bandOf, earnedSignals, rawScore, scoreOf, signed } from '../../utils/prospects';
import * as S from './styled';

const FACTOR_COUNT = SCORE_MODEL.reduce((n, g) => n + g.factors.length, 0);
const weightColor = (w, on = true) => (!on ? P.subtle : w < 0 ? P.danger : P.success);

export default function ScoreBreakdown({ prospect, onToggle }) {
  const [open, setOpen] = useState(false);
  const earned = earnedSignals(prospect);
  const raw = rawScore(prospect);
  const score = scoreOf(prospect);
  const band = bandOf(score);
  const clamped = raw !== score;

  return (
    <>
      <S.ScoreHead $color={band.color}>
        <div>
          <strong>{score.toFixed(1)}</strong>
          <S.Eyebrow>Lead score</S.Eyebrow>
        </div>
        <aside>
          <span style={pillSt(band.color, { gap: 6 })}>
            <S.Dot $color={band.color} />
            {band.label}
          </span>
          <small>{band.note}</small>
        </aside>
      </S.ScoreHead>

      <S.Signals>
        <header>
          <S.Eyebrow>Signals earned</S.Eyebrow>
        </header>
        {earned.length === 0 && <S.SignalRow as="p">No signals earned yet.</S.SignalRow>}
        {earned.map((f) => (
          <S.SignalRow key={f.key}>
            <div>
              <div>
                <MetaChip color={f.color}>{f.group}</MetaChip>
                {f.label}
              </div>
              <p>{f.how}</p>
            </div>
            <S.Weight $color={weightColor(f.weight)}>{signed(f.weight)}</S.Weight>
          </S.SignalRow>
        ))}
        <S.Total>
          <span>{clamped ? `Raw total ${raw.toFixed(1)} → clamped to 0–10` : 'Total'}</span>
          <span>{score.toFixed(1)}</span>
        </S.Total>
      </S.Signals>

      <S.Model>
        <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <span>
            <strong>What qualifies the lead score</strong>
            <small>
              {FACTOR_COUNT} factors in {SCORE_MODEL.length} groups · {earned.length} earned
            </small>
          </span>
          <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
        </button>
        {open && (
          <div style={{ padding: '4px 0 8px' }}>
            {SCORE_MODEL.map((g) => (
              <div key={g.group}>
                <S.ModelGroup>
                  <MetaChip color={g.color}>{g.group}</MetaChip>
                  up to +{g.cap.toFixed(1)}
                </S.ModelGroup>
                {g.factors.map((f) => {
                  const on = Boolean(prospect.signals?.[f.key]);
                  const icon = on ? <FontAwesomeIcon icon={faCircleCheck} /> : <S.Hollow />;
                  return (
                    <S.Factor key={f.key} $on={on}>
                      {onToggle ? (
                        <button type="button" aria-pressed={on} aria-label={f.label} onClick={() => onToggle(f.key, !on)}>
                          {icon}
                        </button>
                      ) : (
                        <span>{icon}</span>
                      )}
                      <div>
                        <strong>{f.label}</strong>
                        <p>{f.how}</p>
                      </div>
                      <S.Weight $color={weightColor(f.weight, on)}>{signed(f.weight)}</S.Weight>
                    </S.Factor>
                  );
                })}
              </div>
            ))}
            <S.Footnote>{SCORE_FOOTNOTE}</S.Footnote>
          </div>
        )}
      </S.Model>

      {clamped && <S.Clamped>Multiple strong signals stacked here — the underlying total exceeds the display ceiling.</S.Clamped>}
    </>
  );
}
